/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const BehaviorCtor = (typeof globalThis !== "undefined" && (globalThis.BehaviorNode || globalThis.Behavior)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const BEHAVIOR_PROPS = [
  "code",
  "executionIndex",
  "slotRegistry"
];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class BehaviorEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    BEHAVIOR_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class BehaviorAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const CMP = "scriptable-behavior";
    const pending = (this._pending = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      const cur = { ...(el.getAttribute(CMP) || {}) };
      Object.assign(cur, pending);
      Object.keys(pending).forEach(k => delete pending[k]);
      el.setAttribute(CMP, cur);
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      if (e?.detail?.name === CMP) flush();
    });

    const setCmpProp = (k, v) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(CMP);
      if (!attr) {
        pending[k] = v;
        return;
      }
      const cur = { ...attr, [k]: v };
      el.setAttribute(CMP, cur);
    };
    const getCmpProp = k => {
      const el = this.sourceObject?.el;
      if (!el) return pending[k];
      const attr = el.getAttribute(CMP);
      return (attr && attr[k]) ?? pending[k];
    };

    Object.defineProperty(this, "code", {
      get: () => getCmpProp("code"),
      set(v) {
        setCmpProp("code", v || "");
      }
    });

    Object.defineProperty(this, "executionIndex", {
      get: () => getCmpProp("executionIndex"),
      set(v) {
        setCmpProp("executionIndex", v);
      }
    });

    Object.defineProperty(this, "slotRegistry", {
      get: () => getCmpProp("slotRegistry"),
      set(v) {
        setCmpProp("slotRegistry", v);
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of BEHAVIOR_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new BehaviorAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new BehaviorEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

export class BehaviorElement extends ScriptableObject {
  static scriptableType = "behavior";

  constructor(initial = {}) {
    super();

    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      // birth the component with sane defaults
      el.setAttribute("scriptable-behavior", {
        code: initial.code || "",
        executionIndex: initial.executionIndex != null ? initial.executionIndex : 0,
        slotRegistry: initial.slotRegistry || {}
      });

      const obj = this.constructor.adopt(el); // identity-preserving prototype shift
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy adapter + apply props (if any)
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }
      return obj;
    }

    // editor / plain Three
    if (!this.scriptableType) {
      Object.defineProperty(this, "scriptableType", { value: this.constructor.scriptableType, configurable: true });
    }

    const node = createBehaviorElementEditor(initial);
    ensureAdapter(node);

    for (const k of Object.keys(initial)) {
      if (k in node) node[k] = initial[k];
    }

    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public behavior props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone(); // objects with clone method
      } else {
        this[prop] = val;
      }
    };

    for (const p of BEHAVIOR_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    // Build init payload so the new element is born with correct component state
    const init = {};
    for (const p of BEHAVIOR_PROPS) {
      const v = this[p];
      if (v === undefined) continue;

      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    // Prefer factory if available (handles adopt/constructor nuances)
    const Ctor = this.constructor && typeof this.constructor.create === "function" ? this.constructor : BehaviorElement;

    const dest = typeof Ctor.create === "function" ? Ctor.create(init) : new BehaviorElement(init);

    // Write object3D transforms (component sync for A-Frame to be added later)
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    // Copy remaining fields & children
    return dest.copy(this, recursive);
  }

  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor BehaviorNode instance to Scriptable, attach adapter and proxies
function makeEditorBehaviorNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || BehaviorCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: BehaviorElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = BehaviorElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new BehaviorEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new BehaviorEditorAdapter(node, descriptors);
  }

  for (const prop of BEHAVIOR_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
BEHAVIOR_PROPS.forEach(prop => {
  Object.defineProperty(BehaviorElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createBehaviorElementEditor(initial = {}) {
  const BehaviorCtor = globalThis.BehaviorNode;

  if (typeof BehaviorCtor !== "function") {
    throw new Error("BehaviorElementEditor: BehaviorNode constructor not found.");
  }
  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new BehaviorCtor(editor);
  } catch (e) {
    throw new Error(`BehaviorElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorBehaviorNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("behavior", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== BehaviorElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return BehaviorElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorBehaviorNodeScriptable(source);

  return source;
});

/* Optional: nicer DevTools label */
Object.defineProperty(BehaviorElement.prototype, Symbol.toStringTag, { value: "BehaviorElement" });
